/**
 * Domino rendering and layout constants. Canvas is sized 1280 × 720
 * internally; LayoutManager scales the display size to the viewport while
 * preserving aspect. Tile dimensions follow the standard 2:1 ratio.
 */

export const CANVAS_WIDTH = 1280;
export const CANVAS_HEIGHT = 720;

/** Long side of a tile (parallel to the arm direction for non-doubles). */
export const TILE_LONG = 72;
/** Short side of a tile (= half-square edge). */
export const TILE_SHORT = 36;
/** Inner corner radius for the rounded ivory tile face. */
export const TILE_CORNER_RADIUS = 4;
/** Stroke width for the dividing line between the two halves of a tile. */
export const TILE_DIVIDER_WIDTH = 1.5;
/** Pip dot radius. */
export const PIP_RADIUS = 2.4;

/** Distance from the canvas edge to the chain region. */
export const CHAIN_REGION_INSET = 16;

/**
 * Wooden border frame thickness (canvas px). Sized to fit the turn banner
 * inside the top frame without overlapping the playfield; all four sides
 * share the same width for visual symmetry. Hand zones and the chain region
 * are insetted by this amount on every side.
 */
export const BOARD_BORDER_THICKNESS = 44;

/**
 * Wood lip between each occupied hand-zone tray and the chain region. The
 * exposed wood + the chain-region recess bevel make the play surface read as
 * sunk below the surrounding shelf where the trays sit.
 */
export const HAND_ZONE_INNER_GAP = 16;

/** Hand zone height (south/north) or width (west/east). */
export const HAND_ZONE_THICKNESS = 112;

/**
 * Scale applied to tiles in the local player's rack. Chain tiles and
 * opponent-stack tiles still use TILE_LONG/TILE_SHORT directly — this only
 * inflates the player's own hand so it's easier to read.
 */
export const RACK_TILE_SCALE = 1.3;

/** Spacing between tiles in the local rack. */
export const RACK_GAP = 6;

/** Spacing between face-down stacked tiles in opponent hands. */
export const STACK_OFFSET = 14;

/** Animation timings (ms). */
export const TILE_PLAY_DURATION_MS = 280;
export const TILE_DRAW_DURATION_MS = 220;

/** Hover/selected glow radius (px). */
export const TILE_HOVER_GLOW_BLUR = 12;

/**
 * Maximum straight length (canvas px in world space) of a chain arm before
 * it folds 90°. ~6 tile-lengths gives a comfortable visual segment before
 * the layout snakes back. Doubles count as TILE_SHORT toward this limit,
 * non-doubles as TILE_LONG.
 */
export const MAX_SEGMENT_LENGTH = TILE_LONG * 6;

/** Viewport zoom bounds and defaults for the pannable/zoomable board. */
export const VIEWPORT_MIN_ZOOM = 0.5;
export const VIEWPORT_MAX_ZOOM = 2.5;
export const VIEWPORT_DEFAULT_ZOOM = 1.3;
/** Distance (world px) within which a dragged tile snaps to a legal arm endpoint. */
export const DROP_TOLERANCE_WORLD = 80;
/** Padding (world px) added around the chain bbox when fitting to view. */
export const AUTOFIT_PADDING = 40;
/** Tween duration (ms) for animated viewport fits. */
export const AUTOFIT_TWEEN_MS = 250;
/** Zoom multiplier applied per mouse-wheel tick. */
export const WHEEL_ZOOM_STEP = 1.1;
/** Pointer-down → drag-start threshold (canvas px) — tiny taps stay clicks. */
export const DRAG_START_THRESHOLD = 4;
/** Radius of the keyboard focus ring drawn around the chain centre for the lead slot. */
export const LEAD_FOCUS_RING_RADIUS = 80;
